// PURPOSE: Read-only build issue Kanban over authorized mirrors and tickets.
// CONTRACT: Spec 066; ticket state/wave fixes incomplete mirrors; no provider writes.
// DEPENDS: Native records, shared badges and existing design tokens.
// CEILING: Only supplied, server-authorized rows are rendered.
import { Badge } from './components';
import type { NativeRecord } from './types';
import './build-issues.css';

export function mergeBuildIssues(issues: NativeRecord[], tasks: NativeRecord[]): NativeRecord[] {
 const used = new Set<string>();
 const linked = tasks.filter(t => t.data.active !== false && (t.data.github_number || t.data.github_node_id || issues.some(i => i.data.task_id === t.id)));
 const project = (issue: NativeRecord, task?: NativeRecord): NativeRecord => {
  if (!task) return issue;
  used.add(task.id);
  const d = task.data;
  return { ...issue, data: { ...issue.data, name: d.name || issue.data.name,
   state: d.state, milestone_id: 'milestone_id' in d ? d.milestone_id : issue.data.milestone_id,
   number: d.github_number || issue.data.number, url: d.github_url || issue.data.url,
   labels: d.github_labels || issue.data.labels, assignee_login: d.github_assignee_login || issue.data.assignee_login,
   kind: d.kind, size: d.size, github_state_reason: d.github_state_reason,
  } };
 };
 const result = issues.map(issue => project(issue, linked.find(t => t.id === issue.data.task_id ||
  (t.data.github_node_id && t.data.github_node_id === issue.data.github_node_id && t.data.github_repo_id === issue.data.repo_id))));
 return [...result, ...linked.filter(t => !used.has(t.id)).map(t => project({ ...t, model: 'github_issue' }, t))];
}
export function issueColumn(issue: NativeRecord) {
 const remote = String(issue.data.github_state_reason || issue.data.status || '').toLowerCase();
 if (['closed', 'completed', 'not_planned'].includes(remote)) return 'Done';
 const state = String(issue.data.state || issue.data.status || '').toLowerCase().replace(/[ _-]+/g, ' ');
 if (['done', 'closed', 'cancelled', 'completed'].includes(state)) return 'Done';
 if (['open', 'in progress', 'in review', 'doing', 'working'].includes(state)) return 'In progress';
 return 'To do';
}
export function BuildIssueBoard({ issues, waves }: { issues: NativeRecord[]; waves: NativeRecord[] }) {
 if (!issues.length) return <p className="muted">No GitHub issues yet.</p>;
 const group = (issue: NativeRecord) => waves.find(w => w.id === issue.data.milestone_id)?.data.name || (issue.data.milestone_id ? 'Other wave' : 'Pre-wave');
 return <section className="bp-section" aria-label="Build issue board"><h2>GitHub issues</h2><div className="build-issue-board">
  {['To do', 'In progress', 'Done'].map(column => {
   const rows = issues.filter(i => issueColumn(i) === column);
   if (!rows.length) return null;
   const groups = [...new Set(rows.map(i => String(group(i))))].sort((a, b) => a === 'Pre-wave' ? -1 : b === 'Pre-wave' ? 1 : a.localeCompare(b, undefined, { numeric: true }));
   return <section className="build-issue-column" key={column}><header><h3>{column}</h3><span className="muted">{rows.length}</span></header>
    {groups.map(name => <div key={name} className="build-issue-group"><h4>{name}</h4>{rows.filter(i => String(group(i)) === name).map(issue => {
     const d = issue.data; const url = String(d.url || '');
     const labels = [...new Set([...String(d.labels || '').split(',').map(s => s.trim()).filter(Boolean), ...[d.size, d.kind].filter(Boolean).map(String)])];
     const content = <><span className="muted">#{String(d.number || '?')}</span><strong>{String(d.name || 'Issue')}</strong><div className="build-issue-labels">{labels.map(label => <Badge key={label}>{label}</Badge>)}</div><small>{String(d.assignee_login || 'Unassigned')}</small></>;
     return /^https?:\/\//i.test(url) ? <a className="build-issue-card" key={issue.id} href={url} target="_blank" rel="noopener noreferrer">{content}</a> : <div className="build-issue-card" key={issue.id}>{content}</div>;
    })}</div>)}
   </section>;
  })}
 </div></section>;
}
